// LICENSE_CODE ZON
'use strict'; /*jslint ts:true, react:true*/
import React, {Fragment, useState} from 'react';
import {Editor} from '../../../shared/editor/editor';
import {Button} from '../../../shared/button';
import {get_img_url} from './utils';
import {Accordion_panel} from '../ui/accordion/accordion';
import styles from './bounty_security_guidelines.module.less';

const check_src = 'bounty_small_check.svg';
const background_src = 'bg_bounty_cta.png';

export interface i_bounty_guideline_group {
  title: string;
  description?: string;
  panel_title?: string;
  items?: string[];
  visible_items?: number;
  show_text?: string;
  hide_text?: string;
  reversed?: boolean;
}

export interface i_bounty_security_guidelines_props {
  groups?: i_bounty_guideline_group[];
}

export const Bounty_security_guidelines = ({
  groups=[],
}: i_bounty_security_guidelines_props)=>{
  const [opened, set_opened] = useState<number[]>([]);

  const toggle_group = (index: number)=>{
    set_opened(current=>current.includes(index)
      ? current.filter(item=>item!=index)
      : [...current, index]);
  };

  return <section className={styles.bounty_security_guidelines}>
    <img
      className={styles.bounty_security_guidelines__background}
      src={get_img_url(background_src)}
      alt="bg"
      aria-hidden={true}
    />

    <div className={styles.bounty_security_guidelines__inner}>
      {groups.map((group, index)=>{
        const is_open = opened.includes(index);
        const visible_count = group.visible_items||3;
        const items = group.items||[];
        const visible_items = items.slice(0, visible_count);
        const hidden_items = items.slice(visible_count);
        const list_id = `bounty-guidelines-${index}`;
        const render_item = (item: string, item_index: number)=><li
          key={`${item}-${item_index}`}
          className={styles.bounty_security_guidelines__item}>
          <span className={styles.bounty_security_guidelines__check_box}>
            <img
              className={styles.bounty_security_guidelines__check}
              src={get_img_url(check_src)}
              alt=""
              aria-hidden={true}
            />
          </span>
          <span>{item}</span>
        </li>;
        return <article
          key={`${group.title}-${index}`}
          className={styles.bounty_security_guidelines__group}
          data-reversed={group.reversed||undefined}>
          <div className={styles.bounty_security_guidelines__content}>
            <h3 className={styles.bounty_security_guidelines__title}>
              {group.title}
            </h3>
            {group.description&&<p
              className={styles.bounty_security_guidelines__description}>
              {group.description}
            </p>}
          </div>
          <div className={styles.bounty_security_guidelines__panel}>
            {group.panel_title&&<p
              className={styles.bounty_security_guidelines__panel_title}>
              {group.panel_title}
            </p>}
            {!!visible_items.length&&<ul
              className={styles.bounty_security_guidelines__list}>
              {visible_items.map(render_item)}
            </ul>}
            {!!hidden_items.length&&<Accordion_panel
              open={is_open}
              id={list_id}
              content_cls={styles.bounty_security_guidelines__more}>
              <ul className={styles.bounty_security_guidelines__list}>
                {hidden_items.map(render_item)}
              </ul>
            </Accordion_panel>}
            {!!hidden_items.length&&<button
              type="button"
              className={styles.bounty_security_guidelines__toggle}
              aria-expanded={is_open}
              aria-controls={list_id}
              onClick={()=>toggle_group(index)}>
              {is_open
                ? group.hide_text||'Show less'
                : group.show_text||'Show more'}
            </button>}
          </div>
        </article>;
      })}
    </div>
  </section>;
};

interface i_bounty_security_guidelines_editor_props {
  value: i_bounty_security_guidelines_props;
  on_change: (value: i_bounty_security_guidelines_props)=>void;
  on_cancel?: ()=>void;
}

export const Bounty_security_guidelines_editor = (
  props: i_bounty_security_guidelines_editor_props
)=>{
  const [state, set_state] =
    useState<i_bounty_security_guidelines_props>({
      ...props.value,
      groups: props.value?.groups||[],
    });

  const groups = state.groups||[];

  const update_group = (
    index: number,
    group: i_bounty_guideline_group
  )=>
  {
    const next_groups = [...groups];
    next_groups[index] = group;
    set_state({...state, groups: next_groups});
  };

  const update_item = (
    group_index: number,
    item_index: number,
    value: string
  )=>
  {
    const next_groups = [...groups];
    const current_group = next_groups[group_index];
    const items = current_group.items
      ? [...current_group.items]
      : [];

    items[item_index] = value;
    next_groups[group_index] = {
      ...current_group,
      items,
    };
    set_state({...state, groups: next_groups});
  };

  return <Editor<i_bounty_security_guidelines_props>
    value={state}
    editors={[]}
    on_change={set_state}
    on_save={()=>props.on_change(state)}
    on_cancel={props.on_cancel}>
    <Fragment>
      <h4>Guideline sections</h4>
      {groups.map((group, group_index)=><div key={group_index}>
        <Editor<i_bounty_guideline_group>
          title={group.title||`Section ${group_index+1}`}
          value={group}
          editors={[
            {key: 'title', label: 'Title', type: 'text'},
            {
              key: 'description',
              label: 'Description',
              type: 'textarea',
            },
            {
              key: 'panel_title',
              label: 'Panel title',
              type: 'text',
            },
            {
              key: 'visible_items',
              label: 'Initially visible items',
              type: 'number',
            },
            {
              key: 'show_text',
              label: 'Show button text',
              type: 'text',
            },
            {
              key: 'hide_text',
              label: 'Hide button text',
              type: 'text',
            },
            {
              key: 'reversed',
              label: 'Reverse layout',
              type: 'boolean',
            },
          ]}
          on_change={(value: i_bounty_guideline_group)=>
            update_group(group_index, value)}>
          <Fragment>
            <h5>Items</h5>
            {(group.items||[]).map((item, item_index)=><div
              key={item_index}>
              <Editor<{text: string}>
                value={{text: item}}
                editors={[
                  {key: 'text', label: 'Item', type: 'text'},
                ]}
                on_change={(value: {text: string})=>
                  update_item(group_index, item_index, value.text)}
              />
            </div>)}
          </Fragment>
        </Editor>
        <Button
          caption="Delete section"
          size="xs"
          color="secondary"
          on_click={()=>set_state({
            ...state,
            groups: groups.filter((_, index)=>index!=group_index),
          })}
        />
        <hr/>
      </div>)}
      <Button
        caption="Add section"
        size="xs"
        on_click={()=>set_state({
          ...state,
          groups: [
            ...groups,
            {
              title: '',
              description: '',
              panel_title: '',
              items: [],
              visible_items: 3,
              show_text: 'Show more',
              hide_text: 'Show less',
              reversed: false,
            },
          ],
        })}
      />
    </Fragment>
  </Editor>;
};
